import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { LookupService } from '../../../core/services/lookup.service';
import { ToastService } from '../toast/toast.service';
import { SettingsService } from '../../../core/services/settings.service';
import { SelectOption } from '../searchable-select/searchable-select.component';

export interface ProductFormData {
  id?: number;
  category_id: number;
  name: string;
  sku: string;
  barcode: string;
  description: string;
  price: number;
  cost: number;
  mrp?: number;
  gst_rate: number;
  has_gst: number;
  hsn_code: string;
  stock_quantity: number;
  min_stock_level: number;
  unit: string;
  unit_type: string;
  size: string;
  is_active: number;
  variants?: any[];
}

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss']
})
export class ProductFormComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() product: ProductFormData | null = null;
  @Output() showChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<ProductFormData>();
  @Output() cancelled = new EventEmitter<void>();

  formData: ProductFormData = {
    category_id: 0,
    name: '',
    sku: '',
    barcode: '',
    description: '',
    price: 0,
    cost: 0,
    mrp: 0,
    gst_rate: 0,
    has_gst: 0,
    hsn_code: '',
    stock_quantity: 0,
    min_stock_level: 0,
    unit: 'pcs',
    unit_type: 'pcs',
    size: '',
    is_active: 1
  };

  // Product type: 'normal' or 'variant'
  productType: 'normal' | 'variant' = 'normal';

  unitTypes: { id: number; name: string; code: string }[] = [];
  isLoadingUnits = false;

  categories: any[] = [];
  categoryPinnedOptions: SelectOption[] = [];
  categorySearchCallback = (term: string) =>
    firstValueFrom(this.lookup.searchCategories(term, 50));
  isSaving = false;
  stockMaintenanceEnabled = true;
  barcodeAutoSuggested = false;
  isLoadingBarcode = false;

  // For searchable select (units: small master — load all)
  unitSelectOptions: any[] = [];

  constructor(
    private api: ApiService,
    private lookup: LookupService,
    private toast: ToastService,
    private settingsService: SettingsService
  ) {
    this.stockMaintenanceEnabled = this.settingsService.isStockMaintenanceEnabled();
  }

  ngOnInit(): void {
    this.loadUnitTypes();
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const showOpened = changes['show']?.currentValue === true && changes['show']?.previousValue !== true;

    if (showOpened) {
      this.initForm();
      this.syncUnitFields();
      this.ensureSelectedCategoryOption();
      if (!this.formData.id) {
        this.loadNextBarcodePreview();
      }
      return;
    }

    if (changes['product']) {
      this.initForm();
      this.syncUnitFields();
      this.ensureSelectedCategoryOption();
      if (this.show && !this.formData.id && !(this.formData.barcode || '').trim()) {
        this.loadNextBarcodePreview();
      }
    }
  }

  initForm(): void {
    if (this.product) {
      this.formData = { ...this.product };
      this.productType =
        this.product.variants && this.product.variants.length > 0 ? 'variant' : 'normal';
    } else {
      this.formData = {
        category_id: 0,
        name: '',
        sku: '',
        barcode: '',
        description: '',
        price: 0,
        cost: 0,
        mrp: 0,
        gst_rate: 0,
        has_gst: 0,
        hsn_code: '',
        stock_quantity: 0,
        min_stock_level: 0,
        unit: 'pcs',
        unit_type: 'pcs',
        size: '',
        is_active: 1
      };
      this.productType = 'normal';
    }
    this.barcodeAutoSuggested = false;
    this.syncUnitFields();
    this.ensureSelectedCategoryOption();
  }

  loadNextBarcodePreview(): void {
    if (this.formData.id) {
      return;
    }

    this.isLoadingBarcode = true;
    this.api.get<{ success: boolean; data: { barcode: string } }>('products/product_next_barcode.php').subscribe({
      next: (response) => {
        this.isLoadingBarcode = false;
        if (response.success && response.data?.barcode) {
          this.formData.barcode = response.data.barcode;
          this.barcodeAutoSuggested = true;
        }
      },
      error: (error) => {
        this.isLoadingBarcode = false;
        this.toast.error(error?.error?.message || error?.message || 'Could not load auto barcode');
      }
    });
  }

  regenerateBarcode(): void {
    if (this.formData.id) {
      return;
    }
    this.formData.barcode = '';
    this.barcodeAutoSuggested = false;
    this.loadNextBarcodePreview();
  }

  onBarcodeManualChange(): void {
    this.barcodeAutoSuggested = false;
  }

  private validateBarcodeInput(barcode: string): string | null {
    const trimmed = (barcode || '').trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.length > 100) {
      return 'Barcode must be 100 characters or less';
    }
    if (!/^[A-Za-z0-9\-_.]+$/.test(trimmed)) {
      return 'Barcode may only contain letters, numbers, hyphen, underscore, and dot';
    }
    return null;
  }

  loadUnitTypes(): void {
    this.isLoadingUnits = true;
    this.api.get<{ success: boolean; data: { id: number; name: string; code: string }[] }>(
      'units/unit_list.php',
      { is_active: 1 }
    ).subscribe({
      next: (response) => {
        this.isLoadingUnits = false;
        if (response.success && response.data) {
          this.unitTypes = response.data;
          this.unitSelectOptions = this.unitTypes.map(u => ({
            value: u.code,
            label: `${u.name} (${u.code})`
          }));
          this.syncUnitFields();
        }
      },
      error: () => {
        this.isLoadingUnits = false;
        this.toast.error('Failed to load unit types');
      }
    });
  }

  private deriveQuantityUnitType(code: string): string {
    const c = (code || 'pcs').toLowerCase();
    return ['kg', 'g'].includes(c) ? 'kg' : 'pcs';
  }

  private resolveUnitCode(raw?: string): string {
    const code = (raw || 'pcs').toLowerCase().trim();
    const match = this.unitTypes.find(u => u.code === code);
    if (match) return match.code;
    const byName = this.unitTypes.find(u => u.name.toLowerCase() === code);
    if (byName) return byName.code;
    return this.unitTypes.length ? this.unitTypes[0].code : 'pcs';
  }

  syncUnitFields(): void {
    if (!this.unitTypes.length) return;
    this.formData.unit = this.resolveUnitCode(this.formData.unit);
    this.formData.unit_type = this.deriveQuantityUnitType(this.formData.unit);
  }

  ensureSelectedCategoryOption(): void {
    const categoryId = this.formData.category_id;
    if (!categoryId) {
      this.categoryPinnedOptions = [];
      return;
    }
    this.lookup.getCategoryOption(categoryId).subscribe((opt) => {
      this.categoryPinnedOptions = opt ? [opt] : [];
    });
  }

  onCategorySelect(categoryId: number): void {
    this.formData.category_id = categoryId;
    this.ensureSelectedCategoryOption();
  }

  onUnitChange(unitCode?: string): void {
    if (unitCode) {
      this.formData.unit = unitCode;
    }
    this.formData.unit_type = this.deriveQuantityUnitType(this.formData.unit);
  }

  onProductTypeChange(type: 'normal' | 'variant'): void {
    if (type === 'variant') {
      this.formData.size = '';
      this.formData.price = 0;
      this.formData.cost = 0;
      this.formData.stock_quantity = 0;
      this.formData.min_stock_level = 0;
      return;
    }

    this.formData.size = '';
  }

  onUnitTypeManualChange(): void {
    // Kept for template compatibility; quantity type is derived from unit master.
  }

  close(): void {
    if (this.isSaving) {
      return;
    }
    this.show = false;
    this.showChange.emit(false);
    this.cancelled.emit();
  }

  save(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.isSaving) {
      return;
    }

    if (!this.formData.name || !this.formData.name.trim()) {
      this.toast.error('Product name is required');
      return;
    }

    if (!this.formData.category_id || this.formData.category_id === 0) {
      this.toast.error('Category is required');
      return;
    }

    // Validation based on product type
    if (this.productType === 'normal') {
      if (!this.formData.price || this.formData.price <= 0) {
        this.toast.error('Price is required and must be greater than 0');
        return;
      }
      if (!this.formData.cost || this.formData.cost <= 0) {
        this.toast.error('Cost is required and must be greater than 0');
        return;
      }
      if (!this.unitTypes.some(u => u.code === this.formData.unit)) {
        this.toast.error('Select a valid unit from master');
        return;
      }
    } else {
      this.formData.price = 0;
      this.formData.cost = 0;
      this.formData.size = '';
      this.formData.stock_quantity = 0;
      this.formData.min_stock_level = 0;
    }

    const barcodeError = this.validateBarcodeInput(this.formData.barcode);
    if (barcodeError) {
      this.toast.error(barcodeError);
      return;
    }

    this.isSaving = true;
    this.syncUnitFields();
    const payload = {
      ...this.formData,
      barcode: (this.formData.barcode || '').trim() || undefined
    };
    const isEdit = this.formData.id !== null && this.formData.id !== undefined;
    const endpoint = isEdit ? 'products/product_update.php' : 'products/product_create.php';

    this.api.post(endpoint, payload).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.toast.success(isEdit ? 'Product updated successfully' : 'Product created successfully');
          
          // Emit the saved product data (with ID from response if new)
          const savedProduct = {
            ...payload,
            id: response.data?.id || this.formData.id,
            barcode: response.data?.barcode || payload.barcode
          };
          
          this.saved.emit(savedProduct);
          this.close();
        } else {
          this.toast.error(response.message || 'Failed to save product');
        }
        this.isSaving = false;
      },
      error: (error: any) => {
        this.toast.error(error.error?.message || 'Failed to save product');
        this.isSaving = false;
      }
    });
  }
}
